import React from 'react';

import { Pencil } from 'lucide-react';

import { MemberProfileDTO } from '../../services/apiClient';
import Card from '../common/Card';

const toDisplayDate = (value: Date | string | undefined): string | null => {
  if (value == null) {
    return null;
  }
  // Date only, read as UTC, so the day shown is the day saved in any time zone.
  const date = new Date(
    value instanceof Date ? value : `${String(value).slice(0, 10)}T00:00:00Z`,
  );
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
};

export const DetailRow: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 border-t border-gray-100 py-2.5 first:border-t-0 first:pt-0 dark:border-white/10">
    <dt className="text-sm text-ink-muted dark:text-white/60">{label}</dt>
    <dd
      className={`text-right text-sm ${
        value
          ? 'font-medium text-ink dark:text-white'
          : 'text-ink-muted dark:text-white/50'
      }`}
    >
      {value ?? 'Not set'}
    </dd>
  </div>
);

// Same text-link style the Dashboard uses for its card actions. className lets the profile hide it
// on a phone, where editing lives in Settings.
export const EditLink: React.FC<{
  onClick: () => void;
  className?: string;
}> = ({ onClick, className = 'flex' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} items-center gap-1 text-sm font-medium text-primary-deep hover:underline dark:text-primary`}
  >
    <Pencil className="h-4 w-4" /> Edit
  </button>
);

// Email, mobile, date of birth and gender. On the profile's Account tab (desktop) and on the
// Settings page (the phone's way in).
export const PersonalInfoCard: React.FC<{
  profile: MemberProfileDTO;
  onEdit: () => void;
}> = ({ profile, onEdit }) => (
  <Card
    title="Personal information"
    headerActions={<EditLink onClick={onEdit} />}
  >
    <dl>
      <DetailRow label="Email address" value={profile.email ?? null} />
      <DetailRow label="Mobile" value={profile.phone ?? null} />
      <DetailRow
        label="Date of birth"
        value={toDisplayDate(profile.dateOfBirth)}
      />
      <DetailRow label="Gender" value={profile.genderName ?? null} />
    </dl>
  </Card>
);
